import React from 'react';
import { Link } from 'react-router-dom';
import { useFooterInfo } from './FooterInfoContext';
import './Footer.css';

/** Footer link groups aligned with RCM (Policy / About) plus Yelooo extras */
const FOOTER_INFO_LINKS = {
  policy: [
    { path: '/privacy', label: 'Privacy Policy' },
    { path: '/terms', label: 'Terms Of Use' },
    { path: '/cancellation', label: 'Return Policy' },
    { path: '/shipping', label: 'Shipping & Cancellation Policy' },
    { path: '/security', label: 'Security' },
    { path: '/grievance', label: 'Grievance Redressal' },
    { path: '/epr', label: 'EPR Compliance' },
  ],
  about: [
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact Us' },
    { path: '/faq', label: 'FAQs' },
  ],
  more: [
    { path: '/careers', label: 'Careers' },
    { path: '/stories', label: 'Yelooo Stories' },
    { path: '/press', label: 'Press' },
    { path: '/corporate', label: 'Corporate Information' },
    { path: '/payments', label: 'Payments' },
    { path: '/sitemap', label: 'Sitemap' },
  ],
};

const FooterInfoLink = ({ path, label, onOpen }) => (
  <li>
    <button type="button" className="footer-info-link" onClick={() => onOpen(path)}>
      {label}
    </button>
  </li>
);

const Footer = () => {
  const { openInfoPanel } = useFooterInfo();

  return (
    <footer className="site-footer">
      <div className="footer-top">
        <div className="footer-content">
          <div className="footer-column">
            <h4>Policy</h4>
            <ul>
              {FOOTER_INFO_LINKS.policy.map((item) => (
                <FooterInfoLink key={item.path} {...item} onOpen={openInfoPanel} />
              ))}
            </ul>
          </div>

          <div className="footer-column">
            <h4>About</h4>
            <ul>
              {FOOTER_INFO_LINKS.about.map((item) => (
                <FooterInfoLink key={item.path} {...item} onOpen={openInfoPanel} />
              ))}
            </ul>
          </div>

          <div className="footer-column">
            <h4>More</h4>
            <ul>
              {FOOTER_INFO_LINKS.more.map((item) => (
                <FooterInfoLink key={item.path} {...item} onOpen={openInfoPanel} />
              ))}
            </ul>
          </div>

          <div className="footer-column footer-social-column">
            <h4>Social</h4>
            <div className="social-icons">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://www.youtube.com/@yelooo.in1" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/yelooo.in?igsh=dHg0dWIyOWs4bDZi" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
            </div>
          </div>

          <div className="footer-column address-column">
            <h4>Office Address:</h4>
            <p>
              Yelooo<br />
              Sr. No. 13, Banker Colony,<br />
              Opp. Vaibhavi Hospital,<br />
              Opp. Akashwani, Pune Solapur Road,<br />
              Hadapsar, Pune-28.
            </p>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <div className="footer-bottom-links">
            <Link to="/become-seller" className="footer-bottom-link">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
              </svg>
              Become a Seller
            </Link>
            <Link to="/advertise" className="footer-bottom-link">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Advertise
            </Link>
            <Link to="/gift-cards" className="footer-bottom-link">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2z"/>
              </svg>
              Gift Cards
            </Link>
            <button type="button" className="footer-bottom-link footer-bottom-link-btn" onClick={() => openInfoPanel('/faq')}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
              </svg>
              Help Center
            </button>
          </div>
          <div className="footer-meta">
            <div className="footer-copyright">
              &copy; {new Date().getFullYear()} Yelooo.in
            </div>
            <p className="footer-credit">
              Site created by{' '}
              <a
                href="https://www.uttishta.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Uttishta IT Solutions
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
